import clsx from 'clsx';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import esLocale from 'date-fns/locale/es';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { getAppointmentDisponibility, reserveAppointment } from '../../api';
import { handleApiGlobalError } from '../../helpers/asyncActions';
import { getFormattedTimeFromMinutes } from '../../helpers/time';
import { useGlobalContext } from '../../stores/global';
import Button from '../core/Button';
import { Property } from '../Property/Property';
import Seller from '../Seller/Seller';
import { Swiper, SwiperSlide } from '../Swiper';
import styles from './Appointment.module.scss';
import Modal from './Modal';

export interface Props {
  chatId: string;
  onClose: () => void;
  property: any;
  propertyId: string;
}

const availableDays = [
  new Date(),
  addDays(new Date(), 1),
  addDays(new Date(), 2),
  addDays(new Date(), 3),
  addDays(new Date(), 4),
  addDays(new Date(), 5),
  addDays(new Date(), 6),
].map((date) => ({
  yyyymmdd: format(date, 'yyyy-MM-dd'),
  date: format(date, 'MMM d', { locale: esLocale }),
  day: format(date, 'd'),
  monthName: format(date, 'LLLL', { locale: esLocale }),
  dayName: format(date, 'EEEE', { locale: esLocale }),
}));

const MEETING_INTERVAL = 60;

export default function Visit({
  onClose,
  propertyId,
  property,
  chatId,
}: Props) {
  const [step, setStep] = useState(0);
  const [availableHours, setAvailableHours] = useState<
    { hourLabel: string; minute: number }[]
  >([]);
  const { handleSubmit, register, errors, reset } = useForm();
  const formData = useRef<any>({});
  const { dispatch, isLoading, user } = useGlobalContext();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (!user) {
      dispatch({
        type: 'SET_ALERT_MODAL',
        payload: {
          title: 'Inicia sesión',
          message: 'Para agendar una visita, primero debes iniciar sesión',
          btnLabel: 'INICIAR SESIÓN',
          onClose: (reason) => {
            if (reason === 'closeBtn')
              dispatch({ type: 'SET_LOGIN_MODAL', payload: 'signin' });
          },
        },
      });
    }
  }, []);

  useEffect(() => {
    reset(formData.current);
    wrapperRef.current?.scroll(0, 0);
  }, [step]);

  useEffect(() => {
    if (!user) return;

    dispatch({ type: 'SET_LOADING', payload: true });

    getAppointmentDisponibility(propertyId, availableDays[activeSlide].yyyymmdd)
      .then((response) => {
        const minutes = response.data?.data || [];

        setAvailableHours(
          (minutes as any[])
            .filter((minute) => minute % MEETING_INTERVAL === 0)
            .map((minute: any) => ({
              hourLabel: `${getFormattedTimeFromMinutes(
                minute
              )}/${getFormattedTimeFromMinutes(minute + MEETING_INTERVAL)}`,
              minute,
            }))
        );
      })
      .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));
  }, [activeSlide]);

  const handleBackClick = () => {
    if (step === 0) {
      onClose();
      return;
    }

    setStep(step - 1);
  };

  const handleFormSubmit = (data: any) => {
    if (step === 0 || step === 1) {
      formData.current = { ...formData.current, ...data };
    }

    if (step === 0) {
      setStep(step + 1);
      return;
    }

    if (isLoading) return;

    if (step === 1) {
      dispatch({ type: 'SET_LOADING', payload: true });

      reserveAppointment(propertyId, availableDays[activeSlide].yyyymmdd, {
        meeting: availableHours[formData.current.idxHour]?.minute,
        body: formData.current.message,
        type: formData.current.type,
        information: {
          email: formData.current.email,
          phone: formData.current.phone,
        },
        chatId: chatId,
      })
        .then(() => setStep(step + 1))
        .catch(handleApiGlobalError.bind(null, dispatch))
        .finally(() => dispatch({ type: 'SET_LOADING', payload: false }));

      return;
    }

    if (step === 2) {
      onClose();
    }
  };

  const getErrorMessage = (error: any) => {
    return Boolean(error) ? (
      <p className={styles.error}>Este campo es obligatorio.</p>
    ) : null;
  };

  const appointmentInfo = (
    <>
      <span>{availableDays[activeSlide]?.dayName}</span>
      <span>
        {availableDays[activeSlide]?.day} de{' '}
        {availableDays[activeSlide]?.monthName}
      </span>
      <span>{availableHours[formData.current?.idxHour]?.hourLabel}</span>
    </>
  );

  const triggerSubmit = (...args: any) => {
    handleSubmit(handleFormSubmit)(...args);
  };

  return (
    <Modal
      title="Programa tu visita"
      open
      closeBtn
      onClose={onClose}
      contentRef={wrapperRef}
      containerProps={{ className: styles.container }}
      footer={
        <div className={styles.footerBtns}>
          {step === 0 && (
            <>
              <Button
                label="Cerrar"
                onClick={handleBackClick}
                variant="outlined"
              />
              <Button label="Siguiente" onClick={triggerSubmit} />
            </>
          )}
          {step === 1 && (
            <>
              <Button
                label="Atrás"
                onClick={handleBackClick}
                variant="outlined"
              />
              <Button label="Confirmar" onClick={triggerSubmit} />
            </>
          )}
          {step === 2 && (
            <>
              <Button label="Finalizar" onClick={triggerSubmit} />
            </>
          )}
        </div>
      }
    >
      <div className={styles.content}>
        {step === 0 && (
          <form onSubmit={triggerSubmit}>
            <p className={styles.note}>
              Te pondremos en contacto con un agente local quien te acompañará a
              la visita programada de tu próxima propiedad.
            </p>
            <h2 className={styles.title}>
              PASO 1<span>Selecciona el tipo de visita</span>
            </h2>

            <div className={styles.radioContainer}>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="presencial"
                  ref={register({ required: true })}
                />
                <span>En Persona</span>
              </label>
              <label>
                <input
                  type="radio"
                  name="type"
                  value="virtual"
                  ref={register({ required: true })}
                />
                <span>Virtual</span>
              </label>
            </div>
            {getErrorMessage(errors.type)}

            <h2 className={styles.title}>
              PASO 2<span>Selecciona la fecha y hora</span>
            </h2>
            <div className={styles.swiperContainer}>
              <Swiper
                className={styles.swiper}
                slidesPerView={3}
                navigation={{}}
                centeredSlides
                breakpoints={{
                  0: { slidesPerView: 1 },
                  380: { slidesPerView: 3 },
                }}
                onInit={(swiper) => swiper.slideTo(activeSlide)}
                onSlideChange={(swiper) => setActiveSlide(swiper.activeIndex)}
              >
                {availableDays.map((el) => (
                  <SwiperSlide key={el.date} className={styles.slide}>
                    <div className={styles.slideContent}>
                      <h1>{el.date}</h1>
                      <p>{el.dayName}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            <div style={{ marginBottom: 24 }}>
              <select name="idxHour" ref={register({ required: true })}>
                {availableHours.length === 0 && (
                  <option value="">No hay citas disponibles</option>
                )}
                {availableHours.map((el, idx) => (
                  <option key={idx} value={idx}>
                    {el.hourLabel}
                  </option>
                ))}
              </select>
              {getErrorMessage(errors.idxHour)}
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={triggerSubmit}>
            <div className={styles.description}>
              <h1>TU VISITA</h1>
              <p>{appointmentInfo}</p>
            </div>

            <h2 className={styles.title}>
              PASO 3<span>Ingresar datos de contacto</span>
            </h2>

            <div className={styles.input}>
              <input
                name="phone"
                type="text"
                defaultValue={user?.phone ?? ''}
                ref={register({ required: true })}
                placeholder="Teléfono"
              />
              <i className="fonticons fonticons-phone" />
              {getErrorMessage(errors.phone)}
            </div>

            <div className={styles.input}>
              <input
                name="email"
                type="text"
                defaultValue={user?.email ?? ''}
                ref={register({ required: true })}
                placeholder="Correo Electrónico"
              />
              <i className="fonticons fonticons-email" />
              {getErrorMessage(errors.email)}
            </div>

            <div className={styles.input}>
              <textarea
                name="message"
                placeholder="Mensaje"
                ref={register}
              ></textarea>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={triggerSubmit}>
            <div className={styles.description}>
              <p className={styles.hasIcon}>
                <i className="fonticons fonticons-checked" />
                {appointmentInfo}
              </p>
            </div>

            <h2 className={clsx(styles.title, styles.isHighlighted)}>
              <span>Tu visita ha sido programada</span>
            </h2>

            {property && <Property property={property} />}
            {property?.user && <Seller seller={property.user} />}
          </form>
        )}
      </div>
    </Modal>
  );
}
